"use client";

import React from "react";
import { ArrowRight, ChartBar as BarChart3, CircleCheck as CheckCircle2, ChevronRight, TrendingUp, Users } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from "@/components/ui/breadcrumb";
const metrics = [{
  value: "+193%",
  label: "Monthly Lead Growth",
  detail: "Scaled from 14 to 41 verified leads/month"
}, {
  value: "312",
  label: "Monthly Website Visits",
  detail: "High-intent organic commercial search traffic"
}, {
  value: "Expanded",
  label: "Service Line Expansion",
  detail: "Small offices → corporate offices, business centers & housekeeping"
}];
const highlights = ["Privately-owned company operating ~20 years with prior $5k/mo digital ad spend delivering only 14 leads", "Google Business Profile optimization, schema markup & high-intent commercial keyword research", "Direct consultant strategy and implementation with zero agency bloat"];
export default function CaseStudyHero() {
  const shouldReduceMotion = useReducedMotion();
  const fadeInVariant = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="casestudyhero" data-nav="light" className="relative bg-background text-foreground pt-32 pb-20 md:pt-40 md:pb-28 border-b border-border overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to="/HomePage">Home</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to="/CaseStudiesPage">Case Studies</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>Commercial Cleaning Transformation</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          <motion.div initial="hidden" animate="show" variants={fadeInVariant} className="lg:col-span-7 flex flex-col gap-6">
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant="secondary" className="px-3 py-1 text-xs font-semibold uppercase tracking-wider">
                Case Study
              </Badge>
              <Badge variant="outline" className="px-3 py-1 text-xs font-medium">
                Local &amp; B2B SEO
              </Badge>
              <span className="text-xs text-muted-foreground font-medium">
                Client Sector: Commercial Facility &amp; Cleaning Services
              </span>
            </div>

            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground leading-[1.15]">
              Commercial Cleaning SEO: Scaling Qualified Leads by{" "}
              <span className="text-primary font-serif">+193%</span>
            </h1>

            <p className="text-base sm:text-lg text-muted-foreground leading-relaxed max-w-2xl">
              Operating for ~20 years, this privately-owned commercial cleaning company was spending ~$5,000/month with a previous provider for just 14 monthly leads. Through dedicated Google Business Profile optimization, schema markup, and targeted keyword research, we scaled inquiries nearly 3x to 41 leads/month.
            </p>

            <div className="flex flex-col gap-3 pt-2">
              {highlights.map((item, i) => <div key={item} data-index={i} className="flex items-start gap-3">
                  <CheckCircle2 className="size-5 text-primary shrink-0 mt-0.5" />
                  <span className="text-sm sm:text-base text-foreground font-medium">{item}</span>
                </div>)}
            </div>

            <div className="flex flex-wrap items-center gap-4 pt-4">
              <Button asChild size="lg" className="shadow-sm">
                <Link to="/ContactPage" className="inline-flex items-center gap-2">
                  <span>Book a Free SEO Consultation</span>
                  <ArrowRight className="size-4" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="shadow-xs">
                <Link to="/CaseStudiesPage" className="inline-flex items-center gap-2">
                  <span>View All Case Studies</span>
                  <ChevronRight className="size-4" />
                </Link>
              </Button>
            </div>
          </motion.div>

          <motion.div initial="hidden" animate="show" variants={fadeInVariant} className="lg:col-span-5 flex flex-col gap-6">
            <Card className="bg-card border-border shadow-md overflow-hidden relative">
              <div className="p-6 pb-2 border-b border-border flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <BarChart3 className="size-5 text-primary" />
                  <span className="text-sm font-semibold text-foreground">Verified Performance Snapshot</span>
                </div>
                <Badge variant="default" className="text-xs">
                  Verified Data
                </Badge>
              </div>

              <CardContent className="p-6 grid grid-cols-1 gap-5">
                {metrics.map((metric, i) => <div key={metric.label} data-index={i} className="p-4 rounded-lg bg-background border border-border flex flex-col gap-1 shadow-2xs">
                    <div className="flex items-baseline justify-between">
                      <span className="text-3xl sm:text-4xl font-bold font-serif text-primary tracking-tight">
                        {metric.value}
                      </span>
                      <TrendingUp className="size-5 text-accent" />
                    </div>
                    <span className="text-sm font-semibold text-foreground">{metric.label}</span>
                    <span className="text-xs text-muted-foreground">{metric.detail}</span>
                  </div>)}

                <div className="pt-2 flex items-center justify-between text-xs text-muted-foreground border-t border-border">
                  <div className="flex items-center gap-1.5">
                    <Users className="size-4 text-primary" />
                    <span>Direct Consultant Delivery: Yassine Essaadi</span>
                  </div>
                  <span>Swindon, UK</span>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </div>
    </section>;
}